import { useState, useEffect } from 'react'
import { Search, Settings, UserCheck, UserX } from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  CustomerLookupResponse,
  CustomerLookupSuccessResponse,
  CustomerLookupNotFoundResponse,
  ApiCredentials,
} from '../types/ingestion.types'
import { INGESTION_API } from '@/api/client'

export function CustomerLookup() {
  const [credentials, setCredentials] = useState<ApiCredentials | null>(null)
  const [showCredentialsForm, setShowCredentialsForm] = useState(false)

  const [uniConfig, setUniConfig] = useState('email')
  const [uniValue, setUniValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState<CustomerLookupResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Load credentials from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('fh_api_credentials')
    if (stored) {
      try {
        setCredentials(JSON.parse(stored))
      } catch {
        // Invalid JSON, ignore
      }
    } else {
      setShowCredentialsForm(true)
    }
  }, [])

  const handleSaveCredentials = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const creds: ApiCredentials = {
      keyId: formData.get('keyId') as string,
      secret: formData.get('secret') as string,
      accountId: parseInt(formData.get('accountId') as string, 10),
    }
    localStorage.setItem('fh_api_credentials', JSON.stringify(creds))
    setCredentials(creds)
    setShowCredentialsForm(false)
  }

  const handleLookup = async () => {
    if (!credentials) {
      setError('Please configure API credentials first')
      return
    }

    if (!uniConfig || !uniValue) {
      setError('Both uni config and value are required')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      setResponse(null)

      const requestData = {
        uni_config: uniConfig,
        value: uniValue,
      }

      const startTime = Date.now()

      const res = await fetch(`${INGESTION_API}/customer/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-FH-Key-ID': credentials.keyId,
          'X-FH-Key-Secret': credentials.secret,
        },
        body: JSON.stringify(requestData),
      })

      const duration = Date.now() - startTime
      const data: CustomerLookupResponse = await res.json()

      setResponse(data)

      // Save to request history
      const historyItem = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        method: 'POST' as const,
        endpoint: '/api/v1/customer/lookup',
        status: res.status,
        duration,
        request: requestData,
        response: data,
        error: data.status === 'error' ? (data as any).message : undefined,
      }
      const history = JSON.parse(localStorage.getItem('fh_request_history') || '[]')
      history.unshift(historyItem)
      localStorage.setItem('fh_request_history', JSON.stringify(history.slice(0, 50)))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  if (showCredentialsForm || !credentials) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">Configure API Credentials</h3>
          <p className="text-sm text-blue-700">
            Enter your Ingestion API credentials to lookup customers.
          </p>
        </div>

        <form onSubmit={handleSaveCredentials} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account ID</label>
            <input
              type="number"
              name="accountId"
              required
              defaultValue={credentials?.accountId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="1234"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Key ID</label>
            <input
              type="text"
              name="keyId"
              required
              defaultValue={credentials?.keyId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="your-api-key-id"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
            <input
              type="password"
              name="secret"
              required
              defaultValue={credentials?.secret}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="your-api-secret"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Save Credentials
            </button>
            {credentials && (
              <button
                type="button"
                onClick={() => setShowCredentialsForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Credentials Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            Account ID: {credentials.accountId}
          </div>
          <button
            onClick={() => setShowCredentialsForm(true)}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <Settings className="h-4 w-4" />
            Update Credentials
          </button>
        </div>
      </div>

      {/* Lookup Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Lookup Customer FHID</h3>
        <p className="text-sm text-gray-600 mb-6">
          Find FirstHive ID using customer identifiers like email, phone, or other uni configs.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Uni Config Type
            </label>
            <select
              value={uniConfig}
              onChange={(e) => setUniConfig(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="mobile">Mobile</option>
              <option value="customer_id">Customer ID</option>
              <option value="aadhaar">Aadhaar</option>
              <option value="pan">PAN</option>
              <option value="user_id">User ID</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Type of identifier used to identify the customer
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
            <input
              type="text"
              value={uniValue}
              onChange={(e) => setUniValue(e.target.value)}
              placeholder={`Enter ${uniConfig} value (e.g., user@example.com)`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            />
            <p className="text-xs text-gray-500 mt-1">
              The actual value for the identifier
            </p>
          </div>

          <button
            onClick={handleLookup}
            disabled={isLoading || !uniConfig || !uniValue}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors',
              isLoading || !uniConfig || !uniValue
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
            )}
          >
            <Search className="h-5 w-5" />
            {isLoading ? 'Looking up...' : 'Lookup Customer'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <UserX className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Response Display */}
      {response && response.status === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-3 mb-4">
            <UserCheck className="h-6 w-6 text-green-600" />
            <div>
              <h4 className="font-medium text-green-900">Customer Found!</h4>
              <p className="text-sm text-green-700 mt-1">
                Customer exists in the system
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-green-700 font-medium mb-1">FHID</p>
              <p className="text-sm font-mono bg-white bg-opacity-50 rounded px-3 py-2 border border-green-300">
                {(response as CustomerLookupSuccessResponse).fhid}
              </p>
            </div>

            <div>
              <p className="text-xs text-green-700 font-medium mb-1">Uni Key</p>
              <p className="text-sm font-mono bg-white bg-opacity-50 rounded px-3 py-2 border border-green-300">
                {(response as CustomerLookupSuccessResponse).uni_key}
              </p>
            </div>

            <div>
              <p className="text-xs text-green-700 font-medium mb-1">Created At</p>
              <p className="text-sm bg-white bg-opacity-50 rounded px-3 py-2 border border-green-300">
                {new Date((response as CustomerLookupSuccessResponse).created_at).toLocaleString()}
              </p>
            </div>

            <div>
              <p className="text-xs text-green-700 font-medium mb-1">Updated At</p>
              <p className="text-sm bg-white bg-opacity-50 rounded px-3 py-2 border border-green-300">
                {new Date((response as CustomerLookupSuccessResponse).updated_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {response && response.status === 'not_found' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <UserX className="h-6 w-6 text-yellow-600" />
            <div className="flex-1">
              <h4 className="font-medium text-yellow-900">Customer Not Found</h4>
              <p className="text-sm text-yellow-700 mt-1">
                {(response as CustomerLookupNotFoundResponse).message}
              </p>
              <div className="mt-3">
                <p className="text-xs text-yellow-700 font-medium mb-1">Searched Uni Key</p>
                <p className="text-sm font-mono bg-white bg-opacity-50 rounded px-3 py-2 border border-yellow-300">
                  {(response as CustomerLookupNotFoundResponse).uni_key}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {response && response.status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <UserX className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-800 mt-1">{response.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
